import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  signal,
  effect,
  ViewChild,
  Input,
  inject,
} from '@angular/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { MatIcon } from '../mat-icon/mat-icon';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { extractItemInstanceID } from '../../internal/internal-inventory/inventoryHelper';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogService } from 'primeng/dynamicdialog';
import { BarcodeScannerDialog } from '../barcode-scanner-dialog/barcode-scanner-dialog';
import { ScannerBarcodeFormat } from '../barcode-scanner-dialog/zxing-loader';

export interface CodeScannedEvent {
  code: string;
  itemID: string;
  instanceID: string
}

@Component({
  selector: 'barcode-scanner-search-field',
  imports: [
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MatIcon,
    FormsModule,
    ButtonModule,
    TranslatePipe,
    ProgressSpinnerModule
  ],
  templateUrl: './barcode-scanner-search-field.html',
  styleUrl: './barcode-scanner-search-field.scss',
  providers: [DialogService],
})
export class BarcodeScannerSearchField {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);

  @ViewChild('searchInput') searchInput: ElementRef;

  @Input() placeholder: string;
  @Input() width: string = "100%";
  @Input() disabled: boolean = false;
  @Input() busy: boolean = false;
  /** Barcode formats the camera scanner looks for. Defaults to common 1D codes, QR, DataMatrix and PDF417. */
  @Input() formats: ScannerBarcodeFormat[];

  @Output() onSearch: EventEmitter<string> = new EventEmitter();
  @Output() onCode: EventEmitter<CodeScannedEvent> = new EventEmitter();

  searchFilter = signal('');

  constructor() {
    effect(() => {
      this.onSearch.emit(this.searchFilter());
    });
  }

  reset() {
    this.searchFilter.set('');
    this.searchInput?.nativeElement.focus();
  }

  focus() {
    this.searchInput?.nativeElement.focus();
  }

  blur() {
    this.searchInput?.nativeElement.blur();
  }

  async openScanner() {
    const code = await BarcodeScannerDialog.scanSingle(this.dialogService, {
      header: this.translate.instant('barcodeScanner.title'),
      formats: this.formats,
    });
    if (!code) return;

    this.searchFilter.set(code);
    this.codeScanned(code);
  }

  codeScanned(code: string) {
    this.onCode.emit({code, ...extractItemInstanceID(code)});
  }
}
